/* bm_peakExplorer_share.js - shareable Peak Explorer selection URLs */
(() => {
  'use strict';

  window.BM = window.BM || {};
  if (window.BM.__peakExplorerShareLoaded) return;
  window.BM.__peakExplorerShareLoaded = true;

  const BM_SHARE_VERSION = '1';
  const BM_SHARE_LABEL_DEFAULT = 'Link kopieren';
  const BM_SHARE_LABEL_SUCCESS = 'Link kopiert';
  const BM_SHARE_LABEL_ERROR = 'Kopieren fehlgeschlagen';
  const BM_SHARE_LABEL_RESET_MS = 2400;
  const BM_PRODUCT_FORM_SELECTOR = 'form[action*="/cart/add"]';
  const BM_SHARE_PARAMS = Object.freeze({
    version: 'bm_share',
    longitude: 'bm_lng',
    latitude: 'bm_lat',
    edgeKm: 'bm_edge',
    scale: 'bm_scale',
    rotation: 'bm_rotation',
    snowEnabled: 'bm_snow',
    snowMode: 'bm_snow_mode',
    snowlineM: 'bm_snowline',
    locked: 'bm_locked'
  });

  function bm_peakExplorer_readShareState(urlLike = window.location.href) {
    let url = null;
    try {
      url = new URL(String(urlLike), window.location.href);
    } catch {
      return null;
    }
    if (url.searchParams.get(BM_SHARE_PARAMS.version) !== BM_SHARE_VERSION) return null;

    const longitude = bm_shareNumber(url.searchParams.get(BM_SHARE_PARAMS.longitude));
    const latitude = bm_shareNumber(url.searchParams.get(BM_SHARE_PARAMS.latitude));
    const edgeKm = bm_shareNumber(url.searchParams.get(BM_SHARE_PARAMS.edgeKm));
    const scaleDenom = bm_shareNumber(url.searchParams.get(BM_SHARE_PARAMS.scale));
    const rotationDeg = bm_shareNumber(url.searchParams.get(BM_SHARE_PARAMS.rotation));
    const snowlineM = bm_shareNumber(url.searchParams.get(BM_SHARE_PARAMS.snowlineM));
    const snowEnabled = bm_shareBoolean(url.searchParams.get(BM_SHARE_PARAMS.snowEnabled));
    const snowModeValue = String(
      url.searchParams.get(BM_SHARE_PARAMS.snowMode) || ''
    ).toLowerCase();
    const snowMode = ['off', 'snow', 'glaciers'].includes(snowModeValue)
      ? snowModeValue
      : snowEnabled === true
        ? 'snow'
        : snowEnabled === false
          ? 'off'
          : null;
    const locked = bm_shareBoolean(url.searchParams.get(BM_SHARE_PARAMS.locked));

    if (
      !Number.isFinite(longitude) || longitude < -180 || longitude > 180 ||
      !Number.isFinite(latitude) || latitude < -85.051129 || latitude > 85.051129
    ) {
      return null;
    }

    return Object.freeze({
      center: Object.freeze([longitude, latitude]),
      edgeKm: Number.isFinite(edgeKm) && edgeKm > 0 ? edgeKm : null,
      scaleDenom: Number.isFinite(scaleDenom) && scaleDenom > 0 ? scaleDenom : null,
      rotationDeg: Number.isFinite(rotationDeg) ? rotationDeg : null,
      snowEnabled,
      snowMode,
      snowlineM: Number.isFinite(snowlineM) ? snowlineM : null,
      locked
    });
  }

  function bm_peakExplorer_attachShare({ root, button, getState, onStatus }) {
    if (!root || !button || typeof getState !== 'function') return null;

    const label = button.querySelector('[data-role="share-link-label"]');
    let labelResetTimeoutId = null;

    const setLabel = (text, reset = false) => {
      if (label) label.textContent = text;
      if (labelResetTimeoutId) window.clearTimeout(labelResetTimeoutId);
      labelResetTimeoutId = null;
      if (reset) {
        labelResetTimeoutId = window.setTimeout(() => {
          labelResetTimeoutId = null;
          if (label) label.textContent = BM_SHARE_LABEL_DEFAULT;
        }, BM_SHARE_LABEL_RESET_MS);
      }
    };

    const onClick = async () => {
      const state = getState();
      if (!state) {
        setLabel(BM_SHARE_LABEL_ERROR, true);
        onStatus?.('Der Link konnte nicht erstellt werden.', { isError: true });
        return;
      }

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      try {
        const shareUrl = bm_buildShareUrl(root, state);
        await bm_writeClipboard(shareUrl);
        setLabel(BM_SHARE_LABEL_SUCCESS, true);
        onStatus?.('Link mit den aktuellen Einstellungen kopiert.');
      } catch (error) {
        setLabel(BM_SHARE_LABEL_ERROR, true);
        onStatus?.(
          `Der Link konnte nicht kopiert werden: ${error.message || error}`,
          { isError: true }
        );
      } finally {
        button.disabled = false;
        button.setAttribute('aria-busy', 'false');
      }
    };

    button.addEventListener('click', onClick);
    return () => {
      button.removeEventListener('click', onClick);
      if (labelResetTimeoutId) window.clearTimeout(labelResetTimeoutId);
      if (label) label.textContent = BM_SHARE_LABEL_DEFAULT;
    };
  }

  function bm_buildShareUrl(root, state) {
    const url = new URL(window.location.href);
    const center = Array.isArray(state.center) ? state.center : [];
    const longitude = Number(center[0]);
    const latitude = Number(center[1]);
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
      throw new Error('Die Kartenkoordinaten sind ungültig.');
    }

    url.searchParams.set(BM_SHARE_PARAMS.version, BM_SHARE_VERSION);
    url.searchParams.set(BM_SHARE_PARAMS.longitude, longitude.toFixed(6));
    url.searchParams.set(BM_SHARE_PARAMS.latitude, latitude.toFixed(6));
    bm_setFiniteParam(url, BM_SHARE_PARAMS.edgeKm, state.edgeKm, 3);
    bm_setFiniteParam(url, BM_SHARE_PARAMS.scale, state.scaleDenom, 0);
    bm_setFiniteParam(url, BM_SHARE_PARAMS.rotation, state.rotationDeg, 0);
    bm_setBooleanParam(url, BM_SHARE_PARAMS.snowEnabled, state.snowEnabled);
    bm_setSnowModeParam(url, BM_SHARE_PARAMS.snowMode, state.snowMode);
    bm_setFiniteParam(url, BM_SHARE_PARAMS.snowlineM, state.snowlineM, 0);
    bm_setBooleanParam(url, BM_SHARE_PARAMS.locked, state.locked);

    const variantId = bm_currentVariantId(root);
    if (variantId) url.searchParams.set('variant', variantId);
    return url.toString();
  }

  function bm_currentVariantId(root) {
    const productJsonElement = root.querySelector('script[type="application/json"][data-product]');
    let variantIds = new Set();
    try {
      const product = JSON.parse(productJsonElement?.textContent || '{}');
      variantIds = new Set(
        (product.variants || [])
          .map((variant) => String(variant?.id || ''))
          .filter(Boolean)
      );
    } catch {
      return '';
    }

    const forms = Array.from(document.querySelectorAll(BM_PRODUCT_FORM_SELECTOR));
    for (const form of forms) {
      const variantId = String(form.querySelector('[name="id"]')?.value || '');
      if (variantIds.has(variantId)) return variantId;
    }
    return '';
  }

  async function bm_writeClipboard(text) {
    if (window.isSecureContext && typeof navigator.clipboard?.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch {
        // Der sichtbare Fallback unten deckt Browser ohne Clipboard-Freigabe ab.
      }
    }

    const textarea = document.createElement('textarea');
    const previousFocus = document.activeElement;
    textarea.value = text;
    textarea.readOnly = true;
    textarea.setAttribute('aria-hidden', 'true');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    const wasCopied = document.execCommand('copy');
    textarea.remove();
    previousFocus?.focus?.({ preventScroll: true });
    if (!wasCopied) throw new Error('Der Browser hat den Zugriff auf die Zwischenablage abgelehnt.');
  }

  function bm_setFiniteParam(url, name, value, fractionDigits) {
    const number = Number(value);
    if (Number.isFinite(number)) {
      url.searchParams.set(name, number.toFixed(fractionDigits));
    } else {
      url.searchParams.delete(name);
    }
  }

  function bm_setBooleanParam(url, name, value) {
    if (typeof value === 'boolean') url.searchParams.set(name, value ? '1' : '0');
    else url.searchParams.delete(name);
  }

  function bm_setSnowModeParam(url, name, value) {
    const mode = String(value || '').toLowerCase();
    if (['off', 'snow', 'glaciers'].includes(mode)) url.searchParams.set(name, mode);
    else url.searchParams.delete(name);
  }

  function bm_shareNumber(value) {
    if (value === null || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function bm_shareBoolean(value) {
    if (value === '1') return true;
    if (value === '0') return false;
    return null;
  }

  window.BM.bm_peakExplorer_readShareState = bm_peakExplorer_readShareState;
  window.BM.bm_peakExplorer_attachShare = bm_peakExplorer_attachShare;
})();
